import { supabase } from "@/integrations/supabase/client";
import { MentorshipOffering } from "@/types/mentorOffering";

/**
 * Fetches all active mentorship offerings across all mentors with related data
 * @returns Array of mentorship offerings with mentor and game details
 */
export const fetchAllMentorshipOfferings = async (): Promise<MentorshipOffering[]> => {
  console.log("Fetching all active mentorship offerings...");
  
  try {
    const { data, error } = await supabase
      .from("mentorship_offerings")
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .eq("is_active", true)
      .order("display_order");
    
    if (error) {
      console.error("Error fetching mentorship offerings:", error);
      throw error;
    }

    console.log(`Fetched ${data.length} active mentorship offerings`);
    
    // Enhanced logging to debug profile data
    if (data && data.length > 0) {
      data.forEach(offering => {
        console.log(`Mentor ID: ${offering.mentor_id}, Mentor profile:`, offering.mentor?.profiles);
      });
    }

    // Map the data to our standardized format
    const offerings = data.map(item => {
      // Extract mentor name with fallback
      const mentorName = item.mentor?.profiles?.nickname || 
                        `Mentor ${item.mentor_id?.substring(0, 4)}`;
      
      return {
        id: item.id,
        title: item.title,
        description: item.description,
        price_cents: item.price_cents,
        duration_minutes: item.duration_minutes,
        is_active: item.is_active,
        is_group: item.is_group,
        max_participants: item.max_participants,
        display_order: item.display_order,
        cover_image_url: item.cover_image_url,
        game_id: item.game_id,
        game: item.game,
        mentor_id: item.mentor_id,
        mentor_name: mentorName,
        mentor_image_url: item.mentor?.image_url,
        mentor_slug: item.mentor?.profiles?.slug,
        created_at: item.created_at
      };
    });

    console.log(`Processed mentorship offerings data: ${offerings.length}`);
    return offerings;
  } catch (error) {
    console.error("Error in fetchAllMentorshipOfferings:", error);
    throw error;
  }
};

/**
 * Fetches a single mentorship offering by ID with related data
 * @param offeringId - The ID of the mentorship offering to fetch
 * @returns A single mentorship offering with mentor and game details, or null if not found
 */
export const fetchMentorshipOfferingById = async (offeringId: string): Promise<MentorshipOffering | null> => {
  console.log(`Fetching mentorship offering with ID: ${offeringId}`);

  try {
    const { data, error } = await supabase
      .from("mentorship_offerings")
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .eq("id", offeringId)
      .single();

    if (error) {
      console.error(`Error fetching mentorship offering with ID ${offeringId}:`, error);
      throw error;
    }

    if (!data) {
      console.log(`Mentorship offering with ID ${offeringId} not found`);
      return null;
    }

    console.log(`Fetched mentorship offering with ID ${offeringId}:`, data);

    // Extract mentor name with fallback
    const mentorName = data.mentor?.profiles?.nickname ||
      `Mentor ${data.mentor_id?.substring(0, 4)}`;

    // Map the data to our standardized format
    const offering: MentorshipOffering = {
      id: data.id,
      title: data.title,
      description: data.description,
      price_cents: data.price_cents,
      duration_minutes: data.duration_minutes,
      is_active: data.is_active,
      is_group: data.is_group,
      max_participants: data.max_participants,
      display_order: data.display_order,
      cover_image_url: data.cover_image_url,
      game_id: data.game_id,
      game: data.game,
      mentor_id: data.mentor_id,
      mentor_name: mentorName,
      mentor_image_url: data.mentor?.image_url,
      mentor_slug: data.mentor?.profiles?.slug,
      created_at: data.created_at
    };

    console.log(`Processed mentorship offering data for ID ${offeringId}`);
    return offering;
  } catch (error) {
    console.error(`Error in fetchMentorshipOfferingById for ID ${offeringId}:`, error);
    throw error;
  }
};
